/**
 * Notification event catalog — the emittable events and their per-event metadata.
 *
 * Add a tenant event: a definition file carrying a `tenant` descriptor, a line in
 * {@link TENANT_EVENT_DEFINITIONS}, renderers in `render/`. Add a platform event: a definition file
 * carrying a `system` descriptor and a line in {@link SYSTEM_EVENT_DEFINITIONS} — the screen and both
 * generic renderers read it from there, so nothing else changes.
 */

import type {
  AnyNotificationEventDefinition,
  AnyPlatformEventDefinition,
  AnyTenantEventDefinition,
  SystemEventDescriptor,
} from './definition'
import { humanHandoverEventDefinition } from './human-handover'
import {
  billingBalanceDepletedEventDefinition,
  billingLowBalanceEventDefinition,
} from './billing-balance'
import { billingSpendingCapEventDefinition } from './billing-spending-cap'
import { billingTopupCreditedEventDefinition } from './billing-topup-credited'
import { billingCreditsExpiringEventDefinition } from './billing-credits-expiring'
import { billingPlanExpiredEventDefinition } from './billing-plan-expired'
import { shopifyDataRequestEventDefinition } from './shopify-data-request'
import { pausedConversationEventDefinition } from './paused-conversation'
import { messageAgentBlockedEventDefinition } from './message-agent-blocked'
import { conversationQualityDigestEventDefinition } from './conversation-quality-digest'
import { platformTenantRegisteredEventDefinition } from './platform-tenant-registered'
import { platformTenantPlanChangedEventDefinition } from './platform-tenant-plan-changed'
import { platformTenantTopupEventDefinition } from './platform-tenant-topup'
import { platformTenantArchivedEventDefinition } from './platform-tenant-archived'

export type {
  NotificationEventDefinition,
  AnyNotificationEventDefinition,
  PlatformEventDefinition,
  PlatformEventPayloadBase,
  SystemEventDescriptor,
  SystemEventFact,
  SystemEventSummary,
  TenantEventDefinition,
  TenantEventDescriptor,
  TenantEventParameter,
  TenantNotificationRecipients,
} from './definition'
export * from './human-handover'
export * from './billing-balance'
export * from './billing-spending-cap'
export * from './billing-topup-credited'
export * from './billing-credits-expiring'
export * from './billing-plan-expired'
export * from './shopify-data-request'
export * from './paused-conversation'
export * from './message-agent-blocked'
export * from './conversation-quality-digest'
export * from './platform-tenant-registered'
export * from './platform-tenant-plan-changed'
export * from './platform-tenant-topup'
export * from './platform-tenant-archived'

/**
 * Platform-operator events, in the order the System Notifications screen lists them.
 * `satisfies` makes a definition without a `system` descriptor a compile error.
 */
const SYSTEM_EVENT_DEFINITIONS = {
  platform_tenant_registered: platformTenantRegisteredEventDefinition,
  platform_tenant_plan_changed: platformTenantPlanChangedEventDefinition,
  platform_tenant_topup: platformTenantTopupEventDefinition,
  platform_tenant_archived: platformTenantArchivedEventDefinition,
} satisfies Record<string, AnyPlatformEventDefinition>

/** Event types configurable on the System Notifications screen. */
export type SystemEventType = keyof typeof SYSTEM_EVENT_DEFINITIONS

export const SYSTEM_EVENT_TYPES = Object.keys(SYSTEM_EVENT_DEFINITIONS) as SystemEventType[]

/**
 * Events tenants receive, in the order the Tenant notifications tab lists them.
 * `satisfies` makes a definition without a `tenant` descriptor a compile error.
 */
const TENANT_EVENT_DEFINITIONS = {
  billing_low_balance: billingLowBalanceEventDefinition,
  billing_balance_depleted: billingBalanceDepletedEventDefinition,
  billing_spending_cap_reached: billingSpendingCapEventDefinition,
  billing_topup_credited: billingTopupCreditedEventDefinition,
  billing_credits_expiring: billingCreditsExpiringEventDefinition,
  billing_plan_expired: billingPlanExpiredEventDefinition,
  message_agent_blocked: messageAgentBlockedEventDefinition,
  human_handover: humanHandoverEventDefinition,
  paused_conversation: pausedConversationEventDefinition,
  shopify_data_request: shopifyDataRequestEventDefinition,
  conversation_quality_digest: conversationQualityDigestEventDefinition,
} satisfies Record<string, AnyTenantEventDefinition>

export type TenantEventType = keyof typeof TENANT_EVENT_DEFINITIONS

export const TENANT_EVENT_TYPES = Object.keys(TENANT_EVENT_DEFINITIONS) as TenantEventType[]

/** Every registered event type. */
export type NotificationEventType = SystemEventType | TenantEventType

const EVENT_DEFINITIONS: Record<NotificationEventType, AnyNotificationEventDefinition> = {
  ...SYSTEM_EVENT_DEFINITIONS,
  ...TENANT_EVENT_DEFINITIONS,
}

/** Look up an event definition, or `undefined` for an unknown type. */
export function getEventDefinition(
  eventType: NotificationEventType,
): AnyNotificationEventDefinition | undefined {
  return EVENT_DEFINITIONS[eventType]
}

export function isSystemEventType(value: unknown): value is SystemEventType {
  return typeof value === 'string' && Object.hasOwn(SYSTEM_EVENT_DEFINITIONS, value)
}

/** Operator-facing metadata for a system event. Total — a system event always has one. */
export function getSystemEventDescriptor(
  eventType: SystemEventType,
): SystemEventDescriptor<unknown> {
  return SYSTEM_EVENT_DEFINITIONS[eventType].system
}

export function isTenantEventType(value: unknown): value is TenantEventType {
  return typeof value === 'string' && Object.hasOwn(TENANT_EVENT_DEFINITIONS, value)
}

/** A tenant event's definition. Total — a tenant event always has its descriptor. */
export function getTenantEventDefinition(eventType: TenantEventType): AnyTenantEventDefinition {
  return TENANT_EVENT_DEFINITIONS[eventType]
}
